import { useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { CHAT_TRIP_PLANNER_NEW_PATH, CHAT_TRIP_PLANNER_PATH } from 'tripPlanner/config'
import { STAGED_CHAT_PROMPT_QUERY_PARAM } from 'tripPlanner/utils/stagedChatPrompt'

export const CHAT_TRIP_PLANNER_ROUTE = CHAT_TRIP_PLANNER_PATH
export const NEW_CHAT_ROUTE_SEGMENT = 'new' as const

export type ChatNavigationOptions = {
  shouldUpdateUrl?: boolean
  shouldReplaceUrl?: boolean
  search?: string
}

interface UseChatNavigationArgs {
  routeChatId: string | undefined
}

interface UseChatNavigationReturn {
  chatId: number | undefined
  setChatId: (nextChatId: number | undefined, options?: ChatNavigationOptions) => void
  setChatIdForStreaming: (nextChatId: number | undefined) => void
  isActiveChatNewlyCreated: boolean
  isNewChatRoute: boolean
  isLandingRoute: boolean
  hasRouteChatId: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
}

export function useChatNavigation({
  routeChatId,
}: UseChatNavigationArgs): UseChatNavigationReturn {
  const [chatId, setChatIdState] = useState<number | undefined>(undefined)
  const [newlyCreatedChatId, setNewlyCreatedChatId] = useState<number | undefined>(undefined)
  const dispatch = useAppDispatch()
  const { search } = useLocation()

  const isNewChatRoute = routeChatId === NEW_CHAT_ROUTE_SEGMENT
  const normalizedRouteChatId = !isNewChatRoute && routeChatId ? Number(routeChatId) : undefined
  const hasRouteChatId = Number.isFinite(normalizedRouteChatId)
  const isLandingRoute = !routeChatId
  const existingRouteChatId = hasRouteChatId ? normalizedRouteChatId : undefined
  const hasStagedChatPromptToken = new URLSearchParams(search).has(STAGED_CHAT_PROMPT_QUERY_PARAM)

  const navigateToChatRoute = useCallback((nextChatId?: number, options?: ChatNavigationOptions) => {
    const nextPath = typeof nextChatId === 'number'
      ? `${CHAT_TRIP_PLANNER_ROUTE}/${nextChatId}`
      : CHAT_TRIP_PLANNER_ROUTE
    if (options?.shouldReplaceUrl) {
      dispatch(replaceWithRegion(nextPath, options.search))
      return
    }
    dispatch(pushWithRegion(nextPath, options?.search))
  }, [dispatch])

  const navigateToNewChatRoute = useCallback(() => {
    dispatch(pushWithRegion(CHAT_TRIP_PLANNER_NEW_PATH))
  }, [dispatch])

  const updateChatId = useCallback((nextChatId: number | undefined, options?: ChatNavigationOptions) => {
    setChatIdState(nextChatId)
    if (options?.shouldUpdateUrl === false) {
      return
    }
    navigateToChatRoute(nextChatId, options)
  }, [navigateToChatRoute])

  const syncChatId = useCallback((nextChatId: number | undefined, options?: ChatNavigationOptions) => {
    setNewlyCreatedChatId(currentChatId => currentChatId === nextChatId ? currentChatId : undefined)
    updateChatId(nextChatId, options)
  }, [updateChatId])

  const setChatIdForStreaming = useCallback((nextChatId: number | undefined) => {
    if ((isNewChatRoute || isLandingRoute) && typeof nextChatId === 'number') {
      setNewlyCreatedChatId(nextChatId)
    }

    updateChatId(nextChatId, {
      shouldReplaceUrl: isNewChatRoute && hasStagedChatPromptToken && typeof nextChatId === 'number',
    })
  }, [hasStagedChatPromptToken, isLandingRoute, isNewChatRoute, updateChatId])

  const isActiveChatNewlyCreated = chatId !== undefined && newlyCreatedChatId === chatId

  return {
    chatId,
    setChatId: syncChatId,
    setChatIdForStreaming,
    isActiveChatNewlyCreated,
    isNewChatRoute,
    isLandingRoute,
    hasRouteChatId,
    existingRouteChatId,
    navigateToNewChatRoute,
  }
}
